"use client";

import React from "react";

import { BubbleMenu, useCurrentEditor } from "@tiptap/react";

import { HighlightPopover } from "@/components/tiptap-ui/highlight-popover/highlight-popover";
import { MarkButton } from "@/components/tiptap-ui/mark-button/mark-button";
import { UnifiedButton } from "@/components/tiptap-ui/unified-button";
import { cn } from "@/lib/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLink } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { AiButton } from "./AiButton";
import { CommentButton } from "./CommentButton";

export const WriterBubbleMenu = ({ className }: { className?: string }) => {
  const { editor } = useCurrentEditor();

  const handleCreateLink = () => {
    if (!editor) return;

    const { from, to } = editor.state.selection;
    const text = editor.state.doc.textBetween(from, to);

    // Set an empty link, which will trigger the LinkBubbleMenu to appear for editing
    editor
      .chain()
      .focus()
      .insertContent({
        type: "text",
        text: text || "link",
        marks: [{ type: "link", attrs: { href: "" } }],
      })
      .run();
  };

  if (!editor) {
    return null;
  }

  return (
    <BubbleMenu
      editor={editor}
      className={cn(
        "bg-background flex flex-row items-center gap-1 rounded-full border px-1 py-1 shadow-md",
        className,
      )}
      data-orientation="horizontal"
      shouldShow={({ state }) => {
        // Only show when there's a text selection
        const { selection } = state;
        const { empty } = selection;

        // Don't show if selection is empty
        if (empty) return false;

        // Don't show if selection spans multiple blocks
        // const isTextSelection = selection.$from.sameParent(selection.$to);
        // if (!isTextSelection) return false;

        return true;
      }}
    >
      <MarkButton type="bold" />
      <MarkButton type="italic" />
      <MarkButton type="strike" />
      <MarkButton type="code" />
      <MarkButton type="underline" />
      <MarkButton type="superscript" />
      <MarkButton type="subscript" />
      <HighlightPopover />
      <UnifiedButton
        type="button"
        onClick={handleCreateLink}
        data-style="ghost"
        title="Create link"
        disabled={!editor.can().setMark("link")}
      >
        <FontAwesomeIcon icon={faLink} className="tiptap-button-icon" />
      </UnifiedButton>
      <CommentButton />
      <AiButton />
    </BubbleMenu>
  );
};
